import { useNavigate } from 'react-router'
import { useDraggable } from '@dnd-kit/core'
import { OwnerMark } from '@/components/Events'
import { eventTone, whoseCalendar } from '@/components/eventTone'
import { PlusIcon } from '@/components/icons'
import { TaskCard, type TaskCardProps } from '@/components/TaskCard'
import type { ShownEvent } from '@/hooks/useEvents'
import type { People } from '@/hooks/usePeople'
import { useTaskActions } from '@/hooks/useTaskActions'
import { fmtDate } from '@/logic/dates'
import { dayHeading } from '@/logic/labels'
import type { PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useCalendar } from '@/store/calendar'
import { useCategories } from '@/store/categories'
import { openEventCard } from '@/store/ui'
import type { DateStr } from '@/types'
import { addFor } from './shared'
import './DayList.css'

interface DayListProps {
  date: DateStr
  now: Now
  items: PlanItem[]
  /** The day's events, listed above its tasks; Week leaves them out, as its grid shows them. */
  events?: ShownEvent[]
  people: People
  /** Week only: a long press lifts a card, to drop on a day's header. */
  draggable?: boolean
}

/**
 * The selected day under a phone's Month or Week: its heading with a + that
 * adds for the day, its events, then its tasks as the Plan tab's cards.
 */
export function DayList({ date, now, items, events = [], people, draggable = false }: DayListProps) {
  return (
    <section className="dlist" aria-label={fmtDate(date)}>
      <div className="dlist__head">
        <h2 className="dlist__title">{dayHeading(date, now.date)}</h2>
        <button type="button" className="icon-btn dlist__add" aria-label={`Add a task for ${fmtDate(date)}`} onClick={() => addFor(date)}>
          <PlusIcon size={18} weight={2.2} />
        </button>
      </div>
      {events.length > 0 && <EventList events={events} people={people} />}
      {items.length > 0 && (
        <div className="dlist__rows">
          {items.map((item) => (draggable ? <DragCard key={item.key} item={item} day={date} now={now} people={people} /> : <DayCard key={item.key} item={item} day={date} now={now} people={people} />))}
        </div>
      )}
      {!events.length && !items.length && <p className="dlist__empty">Nothing planned</p>}
    </section>
  )
}

function EventList({ events, people }: { events: ShownEvent[]; people: People }) {
  const calendars = useCalendar((s) => s.calendars)
  return (
    <div className="dlist__rows">
      {events.map((ev) => {
        const when = ev.allDay ? 'All day' : `${ev.startTime} – ${ev.endTime}`
        const whose = whoseCalendar(ev, people, calendars.find((c) => c.id === ev.calendarId)?.name ?? 'Calendar')
        return (
          <button key={`${ev.calendarId}:${ev.id}`} type="button" className={`devent ev--${eventTone(ev.owner, people)}`} onClick={() => openEventCard({ calendarId: ev.calendarId, eventId: ev.id })}>
            <span className="devent__text">
              <span className="devent__title">{ev.title}</span>
              <span className="devent__meta">
                {when} · {whose}
              </span>
            </span>
            <OwnerMark ev={ev} people={people} />
          </button>
        )
      })}
    </div>
  )
}

interface CardProps {
  item: PlanItem
  day: DateStr
  now: Now
  people: People
}

/** A card's props, as on the Plan tab; a preview opens read-only. */
function useCardProps({ item, day, now, people }: CardProps): TaskCardProps {
  const navigate = useNavigate()
  const actions = useTaskActions()
  const category = useCategories((s) => (item.task.categoryId ? (s.byId[item.task.categoryId] ?? null) : null))
  const t = item.task
  return {
    item,
    where: { day },
    now,
    people,
    category,
    onOpen: () => navigate(`/task/${t.id}`, { state: item.faint ? { readOnly: true } : undefined }),
    onToggleDone: () => actions.toggleDone(t, item.completion),
    onToggleStep: (stepId, done) => actions.toggleStep(t, stepId, done),
  }
}

function DayCard(props: CardProps) {
  return (
    <div className="dlist__row">
      <TaskCard {...useCardProps(props)} />
    </div>
  )
}

/** A card that a long press (or a few pixels of mouse) lifts; done rows, previews and due markers stay put. */
function DragCard(props: CardProps) {
  const { item } = props
  const cardProps = useCardProps(props)
  const movable = !item.done && !item.faint && !item.dueMark
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({ id: item.key, data: { item }, disabled: !movable })
  return (
    <div
      ref={setNodeRef}
      className={`dlist__row ${movable ? 'dlist__row--drag' : ''} ${isDragging ? 'dlist__row--lifted' : ''}`}
      onContextMenu={movable ? (e) => e.preventDefault() : undefined}
      {...(movable ? listeners : {})}
    >
      <TaskCard {...cardProps} />
      {/* the whole card drags by touch and mouse; this unseen button is the keyboard's way in */}
      {movable && <button type="button" ref={setActivatorNodeRef} className="dlist__handle" aria-label={`Move ${item.task.title} to another day`} {...attributes} />}
    </div>
  )
}
